import { useState, useEffect, useRef, useCallback } from 'react';
import tenantStorage from '../../services/tenantStorage';

const usePendingOrders = (isBusinessDay, businessDaySalesDate, recalculateOrder = null) => {
    const [pendingOrders, setPendingOrders] = useState({});
    const [pendingOrderIds, setPendingOrderIds] = useState({});
    const [printedOrders, setPrintedOrders] = useState({});
    const orderIdsRef = useRef({});

    const clearAll = () => {
        setPendingOrders({});
        setPrintedOrders({});
        setPendingOrderIds({});
        orderIdsRef.current = {};
    };

    const normaliseOrder = (orderData) => {
        orderData.total = parseFloat(orderData.total) || 0;
        orderData.subtotal = parseFloat(orderData.subtotal) || 0;
        orderData.serviceCharge = parseFloat(orderData.serviceCharge) || 0;
        orderData.discount = parseFloat(orderData.discount) || 0;
        orderData.promoDiscountAmount = parseFloat(orderData.promoDiscountAmount) || 0;
        return recalculateOrder ? recalculateOrder(orderData) : orderData;
    };

    const loadPendingOrders = useCallback(async () => {
        try {
            if (!isBusinessDay || !businessDaySalesDate) {
                clearAll();
                return;
            }

            const currentSalesDate = await tenantStorage.getState('salesDate');
            if (!currentSalesDate) {
                clearAll();
                return;
            }

            const { orders, printed, order_ids } = await tenantStorage.getOrders(currentSalesDate);

            const unsubmitted = {};
            const unsubmittedPrinted = {};
            const idMap = {};

            Object.entries(orders || {}).forEach(([key, data]) => {
                if (!data.is_submitted) {
                    const normalised = normaliseOrder(data);
                    unsubmitted[key] = normalised;
                    unsubmittedPrinted[key] = printed[key] || false;
                    idMap[key] = order_ids[key] || data.order_id;
                }
            });

            orderIdsRef.current = idMap;
            setPendingOrders(unsubmitted);
            setPrintedOrders(unsubmittedPrinted);
            setPendingOrderIds(idMap);
        } catch (error) {
            console.error('Failed to load pending orders:', error);
            clearAll();
        }
    }, [isBusinessDay, businessDaySalesDate]);

    const refreshTableOrders = async () => {
        await loadPendingOrders();
    };

    // Auto-load when business day changes
    useEffect(() => {
        loadPendingOrders();
    }, [loadPendingOrders]);

    // Listen for order status changes
    useEffect(() => {
        const handler = (event) => {
            const { newStatus } = event.detail || {};
            if (newStatus === 'completed') {
                loadPendingOrders();
            }
        };
        window.addEventListener('orderStatusChanged', handler);
        return () => window.removeEventListener('orderStatusChanged', handler);
    }, [loadPendingOrders]);

    return {
        pendingOrders, setPendingOrders,
        pendingOrderIds, setPendingOrderIds,
        printedOrders, setPrintedOrders,
        orderIdsRef,
        loadPendingOrders,
        refreshTableOrders,
    };
};

export default usePendingOrders;
